import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { TopBarComponent } from '../top-bar/top-bar.component';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// jQuery is loaded via the vendor scripts; we only use it here for cleanup.
// declare to satisfy TypeScript
declare const $: any;


@Component({
  selector: 'home-main-menu',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, TopBarComponent],
  templateUrl: './home-main-menu.component.html',
  styleUrls: ['./home-main-menu.component.css'],
})
export class HomeMainMenuComponent implements OnInit {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private scrollListener: ((ev: Event) => void) | null = null;
  private resizeListener: ((ev: Event) => void) | null = null;
  private lastViewportWidth = window.innerWidth;

  constructor() {}

  ngOnInit() {
    this.initializeStickyMenu();
    this.initializeNavExpander();
    this.attachResizeListener();
    
    // Reinitialize sticky menu on route navigation
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: any) => {
        // Reset scroll to top when navigating
        window.scrollTo(0, 0);
        
        // Clean up submenu states
        const submenus = document.querySelectorAll('.sub-menu') as NodeListOf<HTMLElement>;
        submenus.forEach((submenu) => {
          submenu.style.display = '';
          submenu.style.maxHeight = '';
          submenu.classList.remove('active');
        });
        
        // Remove expanded class
        const menuItems = document.querySelectorAll('.menu-item-has-children');
        menuItems.forEach((item) => {
          item.classList.remove('expanded');
        });
        
        // Close mobile menu and clear vendor classes
        const rsMenu = document.querySelector('.rs-menu') as HTMLElement;
        if (rsMenu) {
          rsMenu.classList.remove('active', 'rs-menu-open', 'rs-menu-close', 'visible');
          rsMenu.removeAttribute('style');
        }
        // also reset toggle state
        const toggles = document.querySelectorAll('.rs-menu-toggle');
        toggles.forEach(t => t.classList.remove('rs-menu-toggle-open', 'rs-menu-toggle-close'));
        
        // Initialize after scroll reset
        this.initializeStickyMenu();
        // Reinitialize mobile menu and nav expander
        this.initializeNavExpander();
      });
  }

  private attachResizeListener() {
    console.log('attachResizeListener (home) registered, current width', window.innerWidth);
    this.resizeListener = () => {
      const currentWidth = window.innerWidth;
      const crossedThreshold = 
        (this.lastViewportWidth > 768 && currentWidth <= 768) ||
        (this.lastViewportWidth <= 768 && currentWidth > 768);
      
      if (crossedThreshold) {
        this.lastViewportWidth = currentWidth;
        
        // Reset all submenu states when crossing mobile/desktop threshold
        const submenus = document.querySelectorAll('.sub-menu') as NodeListOf<HTMLElement>;
        submenus.forEach((submenu) => {
          submenu.style.display = '';
          submenu.style.maxHeight = '';
          submenu.classList.remove('active');
        });
        
        const menuItems = document.querySelectorAll('.menu-item-has-children');
        menuItems.forEach((item) => {
          item.classList.remove('expanded');
        });
        
        const rsMenu = document.querySelector('.rs-menu') as HTMLElement;
        if (rsMenu) {
          rsMenu.classList.remove('active', 'rs-menu-open', 'rs-menu-close', 'visible');
          rsMenu.removeAttribute('style');
        }
        
        // clear toggle classes too
        const toggles = document.querySelectorAll('.rs-menu-toggle');
        toggles.forEach(t => t.classList.remove('rs-menu-toggle-open', 'rs-menu-toggle-close'));
        // Reinitialize menu handlers for new viewport
        this.initializeNavExpander();
      }
    };

    window.addEventListener('resize', this.resizeListener);
    
    // Clean up on destroy
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        // Dummy subscription to trigger destroy cleanup
      });
  }

  private initializeNavExpander() {
    console.log('initializeNavExpander (home) called');
    setTimeout(() => {
      // Clean up vendor classes and event handlers first
      try {
        if (typeof $ !== 'undefined' && $) {
          $('.rs-menu-toggle').off('click');
          $('.nav-menu span.rs-menu-parent').off('click');
          $('ul.nav-menu div.sub-menu-close').off('click');
          // clear any window-level listeners from vendor script
          $(window).off('load');
          $(window).off('resize');
          console.log('jQuery handlers removed (home)');
        }
      } catch (err) {
        console.warn('jQuery cleanup failed (home)', err);
      }

      const rsMenu = document.querySelector('.rs-menu') as HTMLElement;
      if (rsMenu) {
        rsMenu.classList.remove('rs-menu-open', 'rs-menu-close');
        rsMenu.classList.remove('visible');
        rsMenu.removeAttribute('style');
      }
      const toggles = document.querySelectorAll('.rs-menu-toggle');
      toggles.forEach(t => t.classList.remove('rs-menu-toggle-open', 'rs-menu-toggle-close'));

      const visibleItems = document.querySelectorAll('.visible');
      visibleItems.forEach(v => v.classList.remove('visible'));

      // Mobile hamburger menu toggle
      const menuToggle = document.querySelector('.rs-menu-toggle') as HTMLElement;
      
      if (menuToggle && rsMenu) {
        menuToggle.onclick = (e) => {
          e.preventDefault();
          rsMenu.classList.toggle('active');
          console.log('hamburger clicked, menu active:', rsMenu.classList.contains('active'));
        };
      } else {
        console.warn('menuToggle or rsMenu not found (home)', { menuToggle, rsMenu });
      }

      // Handle submenu toggles on mobile
      const navMenu = document.querySelector('.nav-menu') as HTMLElement;
      if (navMenu) {
        navMenu.onclick = (e: any) => {
          const target = e.target as HTMLElement;
          const menuItem = target.closest('.menu-item-has-children');
          
          if (menuItem && target.tagName === 'A' && window.innerWidth <= 768) {
            e.preventDefault();
            e.stopPropagation();
            
            const submenu = menuItem.querySelector('.sub-menu') as HTMLElement;
            if (submenu) {
              const isActive = submenu.classList.contains('active');
              submenu.classList.toggle('active');
              menuItem.classList.toggle('expanded');
              console.log('submenu toggled for item, active:', !isActive);
            }
          }
        };
      } else {
        console.warn('navMenu element not located (home)');
      }

      // Nav expander for off-canvas menu
      const navExpander = document.getElementById('nav-expander');
      const offCanvasMenu = document.querySelector('.right_menu_togle') as HTMLElement;
      
      if (navExpander && offCanvasMenu) {
        navExpander.onclick = (e) => {
          e.preventDefault();
          offCanvasMenu.classList.add('active');
        };
      }

      // Nav close for off-canvas menu
      const navClose = document.getElementById('nav-close');
      if (navClose && offCanvasMenu) {
        navClose.onclick = (e) => {
          e.preventDefault();
          offCanvasMenu.classList.remove('active');
        };
      }
    }, 100);
  }

  private initializeStickyMenu() {
    // Clean up previous listener
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
    }

    // Use longer setTimeout to ensure DOM is fully updated after route change
    setTimeout(() => {
      const menuArea = document.querySelector('.menu-area.menu-sticky') as HTMLElement;
      if (menuArea) {
        // Reset sticky class so CSS can control the menu state consistently.
        menuArea.classList.remove('sticky', 'fixed');
        
        // Attach the scroll listener
        this.attachStickyMenuListener();
        
        // Trigger scroll check immediately
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        if (scrollTop > 0) {
          this.applyMenuStickyStyles(menuArea);
        }
      } else {
        console.warn('Menu area not found - retrying...');
        // Retry if element not found
        this.retryInitialize();
      }
    }, 300);
  }

  private retryInitialize() {
    setTimeout(() => {
      const menuArea = document.querySelector('.menu-area.menu-sticky') as HTMLElement;
      if (menuArea) {
        this.attachStickyMenuListener();
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        if (scrollTop > 0) {
          this.applyMenuStickyStyles(menuArea);
        }
      }
    }, 300);
  }

  private applyMenuStickyStyles(menuArea: HTMLElement) {
    menuArea.classList.add('sticky');
  }

  private removeMenuStickyStyles(menuArea: HTMLElement) {
    menuArea.classList.remove('sticky');
  }

  private attachStickyMenuListener() {
    const menuArea = document.querySelector('.menu-area.menu-sticky') as HTMLElement;
    if (!menuArea) return;

    let isSticky = false;

    this.scrollListener = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const shouldBeStickyNow = scrollTop > 0;

      if (shouldBeStickyNow && !isSticky) {
        this.applyMenuStickyStyles(menuArea);
        isSticky = true;
      } else if (!shouldBeStickyNow && isSticky) {
        this.removeMenuStickyStyles(menuArea);
        isSticky = false;
      }
    };

    window.addEventListener('scroll', this.scrollListener);
    this.scrollListener(new Event('scroll'));
    
    // Cleanup on destroy
    this.destroyRef.onDestroy(() => {
      if (this.scrollListener) {
        window.removeEventListener('scroll', this.scrollListener);
        this.scrollListener = null;
      }
      if (this.resizeListener) {
        window.removeEventListener('resize', this.resizeListener);
        this.resizeListener = null;
      }
    });
  }
}
